import { useState } from "react";
import validator from "validator";
import { toast } from "@3sc/common-component";
import { loaderStateChange, resetLoaderState } from "../../store/Slices/loading";
import { loginUser, forgotPassword, ssoLogin, handleError, authorizeOkta, validateOKTALogin, HOST, resetPassword } from "../../utils/ApiHandler";
import { STATUS_CODE } from "../../utils/const";
import { generateUrlSafeString, processLogin, sha256Base64UrlEncoded } from "../../helper/commonHelper";

export function useLoginHandlers({ dispatch, navigate, executeRecaptcha, t, setValidityMessage, setShowValidityModal }) {
  const [emailError, setEmailError] = useState("");

  //To check licensing validity through response header
  const checkLicensingValidity = (response) => {
    const validityThreshold = response?.headers?.["alert-when-less-than"];
    const licenseValidity = response?.headers?.["validity"];

    const isAcknowledged = sessionStorage.getItem("licenseAcknowledged");

    if (!validityThreshold || !licenseValidity || isAcknowledged) {
      return true;
    }

    // Check if validity is less than the threshold
    if (Number(licenseValidity) <= Number(validityThreshold)) {
      setValidityMessage(
        `Your subscription is about to expire in ${licenseValidity} days.
            Please renew it for uninterrupted service.`
      );
      setShowValidityModal(true);
      return false;
    }
    return true;
  };

  const getBu = () => {
    return JSON.parse(localStorage.getItem("selected_business_unit") || null);
  }

  const onEffectFn = (path) => {
    if (path.includes("login")) {
      const str = path.slice(path.indexOf("=") + 1, path.indexOf("&"));
      if (str) {
        dispatch(loaderStateChange({ isLoading: true, isParent: true }));
        window.history.pushState({}, null, `${HOST}/scai`);

        (async () => {
          const res = await validateOKTALogin(
            str,
            localStorage.getItem("verifier")
          );
          const selected_business_unit = getBu();
          processLogin(res, dispatch, selected_business_unit);
        })();
      }
    }
  }
  const submitUser = async (email, password) => {
    const isEmailValid = validator.isEmail(email);
    if (!isEmailValid) {
      setEmailError("emailAddressValidation");
      return;
    }
    try {
      dispatch(loaderStateChange({ isLoading: true, isParent: true }));
      const recaptchaToken = await executeRecaptcha("login");
      if (!recaptchaToken) {
        toast.error(t("captchaVerificationFailed"));
        dispatch(resetLoaderState());
        return;
      }
      const res = await loginUser(email, password, recaptchaToken);
      const selected_business_unit = getBu();
      const isLicenseValid = checkLicensingValidity(res);
      if (!isLicenseValid) {
        dispatch(resetLoaderState());
        return;
      }
      processLogin(res, dispatch, selected_business_unit);
    } catch (error) {
      dispatch(resetLoaderState());
      let error_obj = error?.message ? JSON.parse(error?.message) : {};
      if (error_obj.error === "mfa_required" && error_obj.mfa_token !== "") {
        navigate("/scai/login-mfa", {
          state: {
            oob_code: error_obj.oob_code,
            mfa_token: error_obj.mfa_token,
            email: error_obj.email,
          },
        });
      }
    }
  };

  const forgotPasswordSubmit = async (email) => {
    const isEmailValid = validator.isEmail(email);
    if (!isEmailValid) {
      setEmailError("emailAddressValidation");
      return;
    }
    dispatch(loaderStateChange({ isLoading: true, isParent: true }));
    try {
      const recaptchaToken = await executeRecaptcha("forgot_password");
      if (!recaptchaToken) {
        toast.error("Failed to verify reCAPTCHA. Please try again.");
        dispatch(resetLoaderState());
        return;
      }
      const res = await forgotPassword(email, recaptchaToken);
      if (res?.status === 200) {
        const responseCode = res?.data?.RESPONSE_CODE;
        toast.success(t(responseCode));
        navigate(`/`);
      }
    } catch (error) {
      const resErr = error?.response?.data;
      toast.error(t(resErr));
    } finally {
      dispatch(resetLoaderState());
    }
  };

  const handleSSO = async (email) => {
    const isEmailValid = validator.isEmail(email);
    if (!isEmailValid) {
      setEmailError("emailAddressValidation");
      return;
    }
    dispatch(loaderStateChange({ isLoading: true, isParent: true }));
    try {
      const { data, status } = await ssoLogin({ email });
      if (status === STATUS_CODE.OK) {
        localStorage.setItem("sso_credentials", JSON.stringify({
          state: data?.state,
          email: email,
        }));
        const url = new URL(data.auth_url);
        window.location.replace(url);
      } else {
        console.error("Auth URL not found in response");
      }
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(loaderStateChange({ isLoading: false, isParent: false }));
    }
  };

  const onOKTAButtonClick = () => {
    const generatedString = generateUrlSafeString();
    localStorage.setItem("authenticator", "okta");
    localStorage.setItem("verifier", generatedString);
    sha256Base64UrlEncoded(generatedString)
      .then((encodedHash) => {
        localStorage.setItem("challenge", encodedHash);
        authorizeOkta(encodedHash);
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const handleCloseValidityModal = () => {
    setShowValidityModal(false);
    sessionStorage.setItem("licenseAcknowledged", "true");
    submitUser(email, password);
  };

  const ValidityModal = ({ message, onClose }) => (
    <Box className="modal-overlay">
      <Box className="modal-content">
        <Typography className="modal-message">{message}</Typography>
        <Box className="modal-close-button" onClick={onClose}>
          Close
        </Box>
      </Box>
    </Box>
  );

  const handleKeypress = (e, email, password) => {
    if (e.keyCode === 13) {
      dispatch(loaderStateChange({ isLoading: true, isParent: true }));
      submitUser(email, password);
    }
  };

  const resetPasswordSubmit = async ({ payload }) => {
    dispatch(loaderStateChange({ isLoading: true, isParent: true }));
    try {
      const res = await resetPassword(payload);
      if (res?.status === 200) {
        const responseCode = res?.data?.RESPONSE_CODE;
        toast.success(t(responseCode));
      }
    } catch (error) {
      const resErr = error?.response?.data;
      toast.error(t(resErr));
    } finally {
      dispatch(resetLoaderState());
      navigate(`/`);
    }
  }

  return {
    resetPasswordSubmit,
    submitUser, forgotPasswordSubmit, handleSSO,
    emailError, setEmailError, checkLicensingValidity,
    handleCloseValidityModal, onEffectFn, handleKeypress,
    onOKTAButtonClick, ValidityModal
  };
}